import { useEffect, useState } from 'react';
import { BuildingWithConvertedAddresses, Address } from '../../utils/types';
import { fetchSideAddresses, hasFootprint } from '../../HouseMap/sideAddresses';

/**
 * Bounded LRU of building_id → detected footprint addresses, so reopening a
 * building doesn't re-hit Mapbox.
 */
const CACHE_LIMIT = 60;
const cache = new Map<string, Address[]>();

function cacheGet(key: string): Address[] | undefined {
  const v = cache.get(key);
  if (v !== undefined) {
    cache.delete(key);
    cache.set(key, v);
  }
  return v;
}

function cacheSet(key: string, value: Address[]): void {
  if (cache.has(key)) cache.delete(key);
  cache.set(key, value);
  while (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

const isFiniteNum = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n);

function resolveCenter(
  building: BuildingWithConvertedAddresses,
): { lat: number; lng: number } | null {
  const c = building.centroid;
  if (c && isFiniteNum(c.lng) && isFiniteNum(c.lat)) return { lat: c.lat, lng: c.lng };
  const a = building.addresses?.[0]?.coordinates;
  if (a && isFiniteNum(a.lng) && isFiniteNum(a.lat)) return { lat: a.lat, lng: a.lng };
  return null;
}

export interface SideAddressesResult {
  addresses: Address[];
  /** True while the footprint is being resolved + probed. The address heading
   *  should show a loading state until this is false (or an address resolves). */
  loading: boolean;
}

/**
 * Detect a building's street frontages (corner buildings front several streets).
 * Display-only — never persisted. Returns every distinct frontage on the
 * footprint, including the primary; the caller picks out the "other" side(s).
 *
 * Probes the hero-resolved footprint passed in by the caller (the complete,
 * zoom-independent outline the hero already resolved), so there is no second
 * `/building-geometry` round-trip and the address list is identical at every zoom
 * level. With no footprint, sides are empty — strict: without a polygon we can't
 * tell a neighbour's address from this building's. Exposes `loading` so the caller
 * can show a placeholder while the (network) probe is in flight.
 */
export function useSideAddresses(
  building: BuildingWithConvertedAddresses,
  footprint?: GeoJSON.Polygon | GeoJSON.MultiPolygon | null,
): SideAddressesResult {
  const [sides, setSides] = useState<Address[]>([]);
  const [loading, setLoading] = useState(false);
  const id = building.id;

  useEffect(() => {
    const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    if (!token || !id || !hasFootprint(footprint)) {
      setSides([]);
      setLoading(false);
      return;
    }

    const cached = cacheGet(id);
    if (cached) {
      setSides(cached);
      setLoading(false);
      return;
    }

    const center = resolveCenter(building);
    if (!center) {
      setSides([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setSides([]);
    setLoading(true);
    const controller = new AbortController();
    fetchSideAddresses(center, footprint, token, controller.signal)
      .then((result) => {
        if (cancelled) return;
        cacheSet(id, result);
        setSides(result);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, footprint]);

  return { addresses: sides, loading };
}
